import { useState } from 'react'
import { Alert, Badge, Button, Chip, Group, Modal, NumberInput, Stack, Switch, TextInput } from '@mantine/core'
import backend from '../../backend/backend'
import DataList from '../../components/shared/DataList'
import Money from '../../components/shared/Money'
import PageHeader from '../../components/shared/PageHeader'
import { useAction, useResource, useT } from '../../hooks'
import Message from '../../i18n/Message'
import { keys, loadAdminProducts } from '../../store/dataActionCreators'
import { toKobo } from '../../utils/format'

const toNaira = (kobo) => (kobo === null || kobo === undefined ? '' : String(kobo / 100))

export default function Products() {
  const t = useT()
  const { data, reload } = useResource(keys.adminProducts, loadAdminProducts)
  const { busy, run } = useAction()
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState({})
  const [errors, setErrors] = useState({})
  const open = (p) => {
    setEditing(p)
    setErrors({})
    setForm({
      min_deposit_pct: p.min_deposit_pct ?? '', monthly_interest_pct: p.monthly_interest_pct ?? '', min_amount: toNaira(p.min_amount_kobo), max_amount: toNaira(p.max_amount_kobo),
      allowed_terms: (p.allowed_terms || []).map(String), grace_days: p.grace_days, late_fee_pct: p.late_fee_pct, default_after_days: p.default_after_days, is_active: p.is_active,
    })
  }
  const save = async () => {
    const body = {
      monthly_interest_pct: form.monthly_interest_pct === '' ? null : String(form.monthly_interest_pct),
      min_amount_kobo: form.min_amount === '' ? null : toKobo(form.min_amount),
      max_amount_kobo: form.max_amount === '' ? null : toKobo(form.max_amount),
      allowed_terms: form.allowed_terms.map(Number),
      grace_days: Number(form.grace_days), late_fee_pct: String(form.late_fee_pct), default_after_days: Number(form.default_after_days), is_active: form.is_active,
    }
    if (editing.kind === 'device') body.min_deposit_pct = form.min_deposit_pct === '' ? null : Number(form.min_deposit_pct)
    const res = await run(() => backend.updateProduct(editing.id, body).catch((err) => {
      setErrors(err?.fields || {})
      throw err
    }), { success: 'product_saved', onDone: reload })
    if (res) setEditing(null)
  }
  const set = (k) => (v) => setForm({ ...form, [k]: v })
  return (
    <>
      <PageHeader title="products_title" />
      <DataList items={data?.items} empty="empty_list" onRowClick={open}
        columns={[
          { key: 'name', header: 'col_name' },
          { key: 'kind', header: 'col_type', render: (p) => <Message id={`kind_${p.kind}`} /> },
          { key: 'monthly_interest_pct', header: 'col_rate', render: (p) => (p.monthly_interest_pct === null ? '—' : `${p.monthly_interest_pct}%`) },
          { key: 'max_amount_kobo', header: 'col_max', render: (p) => <Money kobo={p.max_amount_kobo} />, mobileHidden: true },
          { key: 'is_active', header: 'col_status', render: (p) => <Badge color={p.is_active ? 'green' : p.configured ? 'gray' : 'orange'}>{t(p.is_active ? 'product_active' : p.configured ? 'product_inactive' : 'product_unconfigured')}</Badge> },
        ]} />
      <Modal opened={!!editing} onClose={() => setEditing(null)} title={editing?.name} centered>
        {editing && (
          <Stack>
            {!editing.configured && <Alert color="orange"><Message id="product_configure_hint" /></Alert>}
            {editing.kind === 'device' && <NumberInput label={t('field_min_deposit_pct')} min={0} max={99} value={form.min_deposit_pct} onChange={set('min_deposit_pct')} error={errors.min_deposit_pct} />}
            <NumberInput label={t('field_rate_pct')} min={0} max={50} decimalScale={3} value={form.monthly_interest_pct} onChange={set('monthly_interest_pct')} error={errors.monthly_interest_pct} />
            <TextInput label={t('field_min_amount')} inputMode="decimal" value={form.min_amount} onChange={(e) => set('min_amount')(e.currentTarget.value)} />
            <TextInput label={t('field_max_amount')} inputMode="decimal" value={form.max_amount} onChange={(e) => set('max_amount')(e.currentTarget.value)} error={errors.max_amount_kobo} />
            <Chip.Group multiple value={form.allowed_terms} onChange={set('allowed_terms')}>
              <Group gap="xs">{['1', '2', '3'].map((n) => <Chip key={n} value={n}>{t('term_months', { count: Number(n) })}</Chip>)}</Group>
            </Chip.Group>
            <NumberInput label={t('field_grace_days')} min={0} value={form.grace_days} onChange={set('grace_days')} />
            <NumberInput label={t('field_late_fee_pct')} min={0} max={100} decimalScale={2} value={form.late_fee_pct} onChange={set('late_fee_pct')} />
            <NumberInput label={t('field_default_days')} min={1} value={form.default_after_days} onChange={set('default_after_days')} />
            <Switch label={t('field_active')} checked={!!form.is_active} onChange={(e) => set('is_active')(e.currentTarget.checked)} error={errors.is_active} />
            <Button loading={busy} onClick={save}><Message id="save" /></Button>
          </Stack>
        )}
      </Modal>
    </>
  )
}
